import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { ReactNode } from 'react'

import { useBridge } from '@onl-web/shared/bridge'
import { useFunnel } from '@onl-web/shared/hooks'

import { AlertModal } from '@/components/ui/AlertModal'
import { Header } from '@/components/ui/Header'
import { useGetPaymentMethods } from '@/payment'
import type { GetSealedOrderDetailResponse } from '@/payment/types'
import { ROUTES } from '@/router/routes'

import { useSealedVinylCta } from '../hooks/useSealedVinylCta'
import type { AddressModalKind } from '../lib/constants'
import { ADDRESS_MODAL, NO_BACK_STEPS, NO_HEADER_STEPS, STEP_TITLE } from '../lib/constants'
import type { SealedVinylFunnelData, SealedVinylStep } from '../lib/types'
import { CompleteStep } from '../steps/CompleteStep'
import { EditQuantityStep } from '../steps/EditQuantityStep'
import { LandingStep } from '../steps/LandingStep'
import { OrderDetailStep } from '../steps/OrderDetailStep'
import { OrderStep } from '../steps/OrderStep'
import { PaymentStep } from '../steps/PaymentStep'

import { FunnelLayout, FunnelMain } from './styles'

interface SealedVinylFunnelProps {
  /** 진입 시 useSealedVinylEntry 가 GET /product-orders/detail 로 받은 활성 주문 상세 */
  activeOrderDetail: GetSealedOrderDetailResponse | null
  entryStep: SealedVinylStep
  /** 카드 등록(navigate) 복귀로 payment 직진입 시 복원할 신청 수량 (resume) */
  resumeQuantity?: number
  /** NICEPAY 카드 등록 복귀 시 캐러셀에서 자동선택할 카드 id (resume) */
  resumeSelectedCardId?: string
}

export function SealedVinylFunnel({
  activeOrderDetail,
  entryStep,
  resumeQuantity,
  resumeSelectedCardId,
}: SealedVinylFunnelProps) {
  const navigate = useNavigate()
  const { callHandler } = useBridge()
  const { step, data, setStep } = useFunnel<SealedVinylStep, SealedVinylFunnelData>(entryStep, {
    ...(activeOrderDetail ? { orderId: activeOrderDetail.orderInfo.orderId } : {}),
    // 카드 등록(navigate) 복귀로 payment 직진입 시 선택했던 수량 복원
    ...(resumeQuantity ? { quantity: resumeQuantity } : {}),
  })

  const [addressModal, setAddressModal] = useState<AddressModalKind | null>(null)

  const { handleCtaClick, isCtaPending } = useSealedVinylCta({
    onPass: () => setStep('order'),
    onAlreadyApplied: () => setStep('detail'),
    onSoldOut: () => setStep('soldOut'),
    onAddressUnregistered: () => setAddressModal('register'),
    onServiceUnavailable: () => setAddressModal('change'),
  })

  // 주소 등록/변경 컨펌 → 앱 주소관리 화면(my/address)으로 이동
  const goToAddressManage = () => {
    void callHandler('onNext', { type: 'app', params: { path: 'my/address' } })
    setAddressModal(null)
  }

  const { data: paymentMethods = [] } = useGetPaymentMethods()
  // 카드 결제 캐러셀엔 카드 타입만 — 계좌이체(ACCOUNT, 예: 토스뱅크)는 제외.
  // (카카오페이 quick 등록도 type='CARD'(pgType='KAKAOPAY')라 유지됨)
  const cards = paymentMethods
    .filter((method) => method.type === 'CARD')
    .map((method) => ({
      id: String(method.id),
      alias: method.alias,
      pgType: method.pgType,
    }))

  const exitWebview = () => {
    void callHandler('onPrevious', { pages: 1 })
  }

  /**
   * step 별 뒤로가기 destination — Funnel 내부 이동은 setStep, 외부 이탈은 onPrevious.
   * 맵에 없는 step(landing/detail/soldOut)은 webview 이탈, NO_BACK_STEPS 는 버튼 자체 없음.
   */
  const backHandlers: Partial<Record<SealedVinylStep, () => void>> = {
    order: () => setStep('landing'),
    payment: () => setStep('order'),
    edit: () => setStep('detail'),
  }
  const onBack = NO_BACK_STEPS.has(step) ? undefined : (backHandlers[step] ?? exitWebview)

  /** step → 화면 렌더. detail/edit 은 activeOrderDetail 가드(없으면 null). */
  const renderStep: Record<SealedVinylStep, () => ReactNode> = {
    landing: () => <LandingStep onCtaClick={handleCtaClick} isCtaPending={isCtaPending} />,
    soldOut: () => <LandingStep onCtaClick={handleCtaClick} soldOut />,
    order: () => (
      <OrderStep initialQuantity={data.quantity} onProceed={(quantity) => setStep('payment', { quantity })} />
    ),
    payment: () => (
      <PaymentStep
        quantity={data.quantity}
        cards={cards}
        initialSelectedCardId={resumeSelectedCardId}
        onNavigateToCardRegister={() => navigate(ROUTES.SEALED_VINYL_CARD_REGISTER)}
        onPaid={() => setStep('complete')}
      />
    ),
    complete: () => <CompleteStep onConfirm={exitWebview} />,
    detail: () =>
      activeOrderDetail ? (
        <OrderDetailStep
          detail={activeOrderDetail}
          onEdit={() => setStep('edit')}
          onCancelled={() => setStep('landing')}
        />
      ) : null,
    edit: () =>
      activeOrderDetail ? (
        <EditQuantityStep detail={activeOrderDetail} onCompleted={() => setStep('detail')} />
      ) : null,
  }

  const showHeader = !NO_HEADER_STEPS.has(step)

  return (
    <FunnelLayout>
      {showHeader && (
        <Header
          title={STEP_TITLE[step]}
          onBack={onBack}
        />
      )}
      <FunnelMain>{renderStep[step]()}</FunnelMain>
      {addressModal && (
        <AlertModal
          isOpen
          onClose={() => setAddressModal(null)}
          title={ADDRESS_MODAL[addressModal].title}
          description={ADDRESS_MODAL[addressModal].description}
          primaryAction={{ label: ADDRESS_MODAL[addressModal].primaryLabel, onClick: goToAddressManage }}
          secondaryAction={{ label: '나중에', onClick: () => setAddressModal(null) }}
        />
      )}
    </FunnelLayout>
  )
}
